import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

import { ExternalLink } from '../global/ExternalLink'
import { Button } from '../global/button/Button'
import { useModalsContext } from '../global/context/ModalsContext'
import { CloseIcon } from '../icons/CloseIcon'
import { NotificationIcon } from '../icons/NotificationIcon'
import { PinIcon } from '../icons/PinIcon'
import { WarningIcon } from '../icons/WarningIcon'
import { Notification, NotificationType } from '../model/Notification'
import { Link } from '../navigation/custom-react-router-dom'
import { GlobalModalsType } from '../routing/GlobalModals'
import { AlertIconWrapper } from '../ui/AlertIconWrapper/AlertIconWrapper'
import { Text, TextSmall } from '../ui/Typography/Typography'
import { getAgeOfDate } from '../utils/date.utils'
import { useWindowResize } from '../utils/domUtils'
import { NotificationText, useNotificationUnread } from '../utils/notifications'
import { isFullUrl } from '../utils/validations'

import styles from './NotificationPreview.module.scss'

export interface NotificationPreviewProps {
  notification: Notification
  open?: () => void
  remove?: () => void
  read?: () => void
  hide?: () => void
  externalMode?: boolean
  className?: string
  cardId?: string
  showCloseModal?: boolean
}

const SideInfo = ({
  isPinned,
  externalMode,
  startDateTime,
}: {
  isPinned: boolean
  externalMode?: boolean
  startDateTime: string
}) => {
  return isPinned && !externalMode ? (
    <PinIcon size={22} color='primary' />
  ) : (
    <div>
      <TextSmall isParagraph className={styles.date}>
        {getAgeOfDate(startDateTime)}
      </TextSmall>
    </div>
  )
}

export const NotificationPreview: React.FC<NotificationPreviewProps> = (props) => {
  const {
    open,
    remove,
    externalMode,
    notification,
    read,
    hide,
    cardId,
    showCloseModal = false,
  } = props
  const { imageUrl, isPinned, ctaText, ctaUrl, isRead } = notification
  const { startDateTime, title, notificationType, text } = notification
  const { refreshAllNotifications } = useNotificationUnread()
  const { setActiveModal, closeModal } = useModalsContext()
  const isMobile = useWindowResize()
  const [url, setUrl] = useState<string>()
  const [isInternalLink, setIsInternalLink] = useState<boolean>(false)

  const anchorTagToUrlMap: Record<string, string> = {
    '#SecuritySettings': '/profile/security-settings',
    '#Deposit': '/dashboard/traders-room/wallets/deposit',
    '#CFDSecuritySettings': '/profile/security-settings',
    '#CFDChangePassword': '/profile/security-settings',
    '#CFDTwoFactorAuthentication': '/profile/security-settings',
    '#CFDAddDepositForm': '/dashboard/traders-room/wallets/deposit',
    '#CFDAddTAForm': '/dashboard/traders-room/trading-accounts/new',
    '#CFDAddCampaignTAForm': '/dashboard/traders-room/trading-accounts/new?campaign=true',
    '#IBDashboard': '/dashboard/introducing-broker/dashboard',
    '#IBWallets': '/dashboard/introducing-broker/wallets',
    '#IBReferralMaterials': '/dashboard/introducing-broker/referral-materials',
    '#CFDPlatforms': '/dashboard/tools/platforms/mt5',
    '#CFDVideoTutorials': '/dashboard/learning/trainings',
    '#CFDPersonalInfo': '/profile/personal-info',
    '#CFDDocumentManagement': '/profile/document-management',
    '#CFDBankAccounts': '/profile/bank-accounts',
    '#CFDPhoneNumbers': '/profile/phone-numbers',
    '#CFDAccountSettings': '/profile/account-settings',
    '#CFDCampaignResults': '/dashboard/traders-room/campaign-results',
    '#CFDWallets': '/dashboard/traders-room/wallets',
    '#CFDTransferFundsForm': '/dashboard/traders-room/wallets/transfer',
  }

  const getUrlFromAnchorTag = (link: string | undefined): string | null => {
    if (!link || !link.startsWith('#')) {
      return null
    }
    return anchorTagToUrlMap[link] || null
  }

  useEffect(() => {
    if (ctaUrl) {
      const internalUrl = getUrlFromAnchorTag(ctaUrl)
      if (internalUrl) {
        setUrl(internalUrl)
        setIsInternalLink(true)
      } else {
        const prefix = !isFullUrl(ctaUrl) ? 'https://' : ''
        setUrl(`${prefix}${ctaUrl}`)
        setIsInternalLink(false)
      }
    }
  }, [ctaUrl])

  const showCta = ctaText && url
  const allowDelete = (!isPinned && isRead && !!remove) || !!hide
  const overflown = text.length > 200 && !!open
  const showSideInfo = isMobile && !hide

  const openNotification = () => {
    if (!open) {
      return
    }
    if (overflown) {
      return open()
    }
    if (read) {
      read()
    }
    if (externalMode) {
      refreshAllNotifications()
    }
  }

  const handleDeleteClick = () => {
    if (showCloseModal) {
      setActiveModal(GlobalModalsType.DeleteNotification, {
        onConfirm: handleConfirmDeleteAction,
      })
    } else {
      handleConfirmDeleteAction()
    }
  }

  const handleConfirmDeleteAction = () => {
    if (hide) {
      hide()
    } else if (remove) {
      remove()
    }
    closeModal()
  }

  const NotificationIconPicker = () => {
    if (notificationType.id === NotificationType.Warning) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='warning' />
        </AlertIconWrapper>
      )
    }
    if (notificationType.id === NotificationType.Error) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='error' />
        </AlertIconWrapper>
      )
    }
    if (notificationType.id === NotificationType.Platform) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='error' />
        </AlertIconWrapper>
      )
    }
    return (
      <AlertIconWrapper isActive={!isRead && !!open}>
        <NotificationIcon size={22} />
      </AlertIconWrapper>
    )
  }

  return (
    <div
      className={classNames(styles.notification, props.className, {
        [styles.external]: externalMode,
        [styles.warning]: notificationType.id === NotificationType.Warning,
        [styles.error]: notificationType.id === NotificationType.Error,
      })}
      id={cardId}
    >
      {!!imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          className={classNames(styles.promoImage, {
            [styles.expanded]: !open,
          })}
        />
      )}
      {!imageUrl && <NotificationIconPicker />}
      <div className={styles.textBox}>
        <div
          className={classNames(styles.titleBox, {
            [styles.promo]: notificationType.id === NotificationType.Promotional,
          })}
        >
          <p
            data-test='open-notification'
            onClick={openNotification}
            className={classNames(styles.title, {
              [styles.active]: open,
            })}
          >
            {title}
          </p>
          <div className={styles.infoWrap}>
            {allowDelete && (
              <div
                data-test='remove-notification'
                className={styles.closeIcon}
                onClick={handleDeleteClick}
              >
                <CloseIcon size={14} />
              </div>
            )}
            {showSideInfo && (
              <div className={styles.datePin}>
                <SideInfo
                  isPinned={isPinned}
                  externalMode={externalMode}
                  startDateTime={startDateTime}
                />
              </div>
            )}
          </div>
        </div>
        {!!imageUrl && (
          <img
            src={imageUrl}
            alt={title}
            className={classNames(styles.mobileImage, {
              [styles.expanded]: !open,
            })}
          />
        )}
        <Text isParagraph className={styles.text}>
          <NotificationText
            overflown={overflown}
            text={text}
            open={open}
            className={styles.readMore}
          />
        </Text>
        {showCta && (
          <div className={styles.buttonBox}>
            {isInternalLink ? (
              <Link to={url || ''} className={styles.externalLink}>
                <Button
                  appearance='primary'
                  size='M'
                  data-test='notification-cta-open'
                  className={styles.close}
                >
                  {ctaText}
                </Button>
              </Link>
            ) : (
              <ExternalLink className={styles.externalLink} url={url}>
                <Button
                  appearance='primary'
                  size='M'
                  data-test='notification-cta-open'
                  className={styles.close}
                >
                  {ctaText}
                </Button>
              </ExternalLink>
            )}
          </div>
        )}
      </div>
      {!externalMode && (
        <div className='is-hidden-mobile'>
          <SideInfo isPinned={isPinned} externalMode={externalMode} startDateTime={startDateTime} />
        </div>
      )}
    </div>
  )
}
